import { get } from "lodash";
import AppContext from "PFApp/app_context";
import { useDetailsPanelContext } from "PFApp/booking/components/details_panel";
import BulkActionsHeader from "PFApp/components/table/bulk_actions_header";
import ColumnsHeaders from "PFApp/components/table/columns_headers";
import NoItems from "PFApp/components/table/no_items";
import { Panel } from "PFApp/components/table/panel";
import ProfileCell from "PFApp/components/table/profile_cell";
import Rows from "PFApp/components/table/rows";
import { DropdownOption } from "PFComponents/dropdown/dropdown";
import DropdownButton from "PFComponents/dropdown/dropdown_button";
import SimplePagination from "PFComponents/pagination/simple_pagination";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import DotsIcon from "PFIcons/vertical_dots.svg";
import { WrappedLoadingDots } from "PFOutlook/src/components/wrapped_loading_dots/wrapped_loading_dots";
import { Booking, BookingApproval } from "PFTypes/booking";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import CategoryBadge from "../../components/category_badge";
import ApprovalStatusBadge from "./approval_status_badge";
import css from "./approvals_list_view.module.scss";
import Counters from "./counters";
import NoPermissions from "./no_permissions";
import { ColumnNames } from "./use_approvals_tab";

const ApprovalsListView = () => {
  const {
    store: {
      pages: {
        booking: {
          approvals: {
            data,
            loaded,
            loading,
            setPage,
            pinnedIds,
            columns,
            openModal,
            onOrderChange,
            onFilterChange
          }
        }
      }
    }
  } = useContext(AppContext);
  const { openDetailsPanel } = useDetailsPanelContext();

  const { t } = useTranslation(["bookingModule", "translation"]);
  const { getCategory } = useBookingCategories();

  const { entries, meta } = data;

  const [selectedItems, setSelectedItems] = useState<BookingApproval[]>([]);
  const selectedIds = useMemo(() => selectedItems.map(({ id }) => id), [selectedItems]);
  const clearSelectedItems = useCallback(
    (items?: BookingApproval[]) => {
      setPage(1);
      items
        ? setSelectedItems((currentItems) =>
            currentItems.filter(({ id }) => !items.map(({ id }) => id).includes(id))
          )
        : setSelectedItems([]);
    },
    [setSelectedItems]
  );

  const entriesUnpinned = entries.filter(({ id }) => !pinnedIds.includes(id)).map(({ activity }) => activity);
  const noItems = pinnedIds.length === 0 && entriesUnpinned.length === 0;

  const isBookingCategoryHiddenOrReadOnly = (booking: Booking) => {
    const category = getCategory(booking);
    return !category || category.readonly;
  };

  const getActions = useCallback(
    (items: BookingApproval[]): DropdownOption[] => {
      const disabled =
        !!items.find(({ state }) => state !== "pending") ||
        !!items.find(({ booking }) => isBookingCategoryHiddenOrReadOnly(booking));
      return [
        {
          id: "approve",
          global_id: "approve",
          displayElement: t("bookingModule:approvals.approve"),
          item: (data) => openModal({ actionType: "approve", data, onSuccess: clearSelectedItems }),
          disabled
        },
        {
          id: "request_approval",
          global_id: "request_approval",
          displayElement: t("bookingModule:approvals.requestApproval"),
          item: (data) =>
            openModal({
              actionType: "requestApproval",
              data,
              onSuccess: clearSelectedItems
            })
        },
        {
          id: "reject_approval",
          global_id: "reject_approval",
          displayElement: t("bookingModule:approvals.rejectApproval"),
          item: (data) =>
            openModal({
              actionType: "rejectApproval",
              data,
              onSuccess: clearSelectedItems
            }),
          disabled
        }
      ];
    },
    [openModal]
  );

  const renderActions = (item) => (
    //@ts-ignore-line
    <DropdownButton
      options={getActions([item])}
      buttonKind="blank"
      buttonStyle={{ height: 18 }}
      style={{ marginLeft: "auto" }}
      handleChange={(fn) => fn && fn(item)}
    >
      <DotsIcon width={20} height={17} />
    </DropdownButton>
  );

  const renderCell = useCallback(
    (item, column) => {
      const category = getCategory(item.booking);
      if (
        !category &&
        [ColumnNames.booking, ColumnNames.category, ColumnNames.profile, ColumnNames.creator].includes(
          column.name
        )
      ) {
        return <NoPermissions />;
      }
      const rawValue = column.attribute ? get(item, column.attribute) : item[column.name];

      if (column.name === ColumnNames.booking) {
        return rawValue ? (
          <button
            className={css.removeStyle}
            onClick={() =>
              openDetailsPanel({
                type: "booking",
                id: item.booking.id
              })
            }
          >
            {rawValue}
          </button>
        ) : (
          "-"
        );
      }
      if (column.name === ColumnNames.engagement || column.name === ColumnNames.role) {
        return rawValue?.id ? (
          <button
            className={css.removeStyle}
            onClick={() =>
              openDetailsPanel({
                type: column.name,
                id: rawValue.id
              })
            }
          >
            {rawValue.name}
          </button>
        ) : (
          "-"
        );
      }
      if (column.name === ColumnNames.category) {
        return <CategoryBadge categoryId={rawValue} />;
      }
      if (column.name === ColumnNames.state) {
        return <ApprovalStatusBadge status={rawValue} />;
      }
      if (column.type === "profile") {
        const approver = rawValue || item.assignee;
        return approver ? (
          <ProfileCell
            profile={approver}
            onClick={() => openDetailsPanel({ type: "profile", id: approver.id })}
          />
        ) : (
          "-"
        );
      }
    },
    [openDetailsPanel]
  );

  return (
    <Panel
      classes={{ header: css.panelHeader, container: css.container }}
      title={t("translation:all")}
      actionsChildren={<Counters meta={meta} onFilterChange={onFilterChange} onChange={clearSelectedItems} />}
    >
      <div className={css.separator} />
      <BulkActionsHeader
        actions={getActions(selectedItems)}
        allItems={data.entries}
        setSelectedItems={setSelectedItems}
        selectedItems={selectedItems}
      />
      <div className={css.separator} />
      {loaded ? (
        <div className={css.scrollWrapper}>
          <div className={css.list}>
            {noItems ? (
              <NoItems itemType="booking" />
            ) : (
              <>
                <ColumnsHeaders
                  columns={columns}
                  onOrderChange={onOrderChange}
                  order={meta.order}
                  classes={{ selectModePadding: css.columnHeadersContainer }}
                />
                <Rows
                  //@ts-ignore
                  items={data.entries}
                  pinnedIds={pinnedIds}
                  selectedIds={selectedIds}
                  setSelectedItems={setSelectedItems}
                  isSelectMode
                  className={css.row}
                  columns={columns}
                  isLoading={loading}
                  renderActions={renderActions}
                  renderCell={renderCell}
                />
              </>
            )}
          </div>
        </div>
      ) : (
        <WrappedLoadingDots className={css.loadingDots} />
      )}
      <div style={{ display: "flex" }}>
        <SimplePagination
          style={{ position: "sticky", left: "50%", transform: "translate(-50%, 0px)" }}
          currentPage={meta.page}
          handleClick={setPage}
          totalPages={meta.totalPages}
          scrollOnClick={false}
        />
      </div>
    </Panel>
  );
};

export default ApprovalsListView;
